import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as URI from 'urijs';

import { AppConfig } from '®shared/config';
import { CurrentUserService } from '®shared/current-user';

const X_REFERER_HEADER = 'X-Referer';

@Injectable()
export class DashboardAnalyticsService {
  private baseUrl: URI;

  constructor(
    private appConfig: AppConfig,
    private currentUserService: CurrentUserService,
    private httpClient: HttpClient,
  ) {
    this.baseUrl = new URI(this.appConfig.tenant.httpsBaseUri);
  }

  /**
   * Get the dashboard event submission endpoint for a list outline view.
   *
   * @param {string} listId - the ID for the list being viewed
   * @return {URI} - URI for the list dashboard submission endpoint
   */
  getListViewDashboardUri(listId: string): URI {
    const listUri = new URI('').segment(['lists', listId]).suffix('html');

    return listUri;
  }

  /**
   * Get the dashboard event submission endpoint for a list item view.
   *
   * @param {string} listId - the ID for the list being viewed
   * @param {string} itemId - the ID for the item being viewed
   * @return {URI} - URI for the list item dashboard submission endpoint
   */
  getItemViewDashboardUri(listId: string, itemId: string): URI {
    const referrer = new URI('/')
      .segment(['lists', listId])
      .suffix('html')
      .hash(`item-${itemId}`)
      .toString();

    const itemUri = new URI('')
      .segment(['items', itemId])
      .suffix('html')
      .search({ referrer });

    return itemUri;
  }

  /**
   * Logs a list outline view to the dashboard.
   *
   * @param {string} listId - the ID for the list being viewed
   */
  logListOutlineView(listId: string): void {
    const listViewUri = this.getListViewDashboardUri(listId);
    this.headRequest(listViewUri);
  }

  /**
   * Get the dashboard event submission endpoint for a list item view.
   *
   * @param {string} listId - the ID for the list being viewed
   * @param {string} itemId - the ID for the item being viewed
   */
  logListItemView(listId: string, itemId: string): void {
    const itemViewUri = this.getItemViewDashboardUri(listId, itemId);
    this.headRequest(itemViewUri);
  }

  /**
   * Get the current URI
   * @return {URI} - URI for the current URI
   */
  getCurrentUri(): URI {
    return new URI();
  }
  /**
   * Makes a HEAD requeat to a dashboard events URL.
   *
   * @param {URI} path - the path for the dashboard submission.
   */
  headRequest(path: URI): void {
    const url = path.origin(this.baseUrl).toString();

    // We are removing the query string from the referer here so that we have
    // a clean URL in order to do analysis at a later date
    const fakeReferer = this.getCurrentUri().search('').toString();

    // Retrieve user header keyed with X-Effective-User
    const optionalUserHeader = this.currentUserService.getUserHeader() || {};
    const referrerHeader = { [X_REFERER_HEADER]: fakeReferer };
    const allRequestHeaders = { ...referrerHeader, ...optionalUserHeader };

    this.httpClient
      .head(url, {
        headers: new HttpHeaders(allRequestHeaders),
        observe: 'response',
      })
      .subscribe({
        next: value => this.onSubmissionSuccess(value),
        error: err => this.onSubmissionFailed(err),
        complete: () => {},
      });
  }

  /**
   * Handle a successful response to an analytics submission.
   *
   * NOTE: this currently does nothing, and is used for testing only.
   *
   * @param {HttpResponse<any>} response - the successful response
   */
  onSubmissionSuccess(_response: HttpResponse<any>): void {}

  /**
   * Handle a failed response to an analytics submission.
   *
   * NOTE: this currently does nothing, and is used for testing only.
   *
   * @param {any} error - an unsuccessful response, or any other error.
   */
  onSubmissionFailed(_error: any): void {}
}
